/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI Article Wizard 4-Step DTOs & Validation Schemas (OpenAPI 3.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Standard Search Intent types for SEO keyword analysis.
 */
export const SearchIntentEnum = z
  .enum(['informational', 'commercial', 'transactional', 'navigational'])
  .openapi({
    description: 'Target SEO search intent classification',
    example: 'informational',
  });

export type SearchIntent = z.infer<typeof SearchIntentEnum>;

// =============================================================================
// STEP 1: KEYWORDS & INTENT SUGGESTION
// =============================================================================

export const WizardKeywordsRequestSchema = z
  .object({
    topic: z
      .string()
      .min(2)
      .max(200)
      .openapi({
        description: 'Core topic or niche to research keywords for',
        example: 'Cloudflare Workers AI SaaS Architecture',
      }),
    targetLanguage: z
      .string()
      .min(2)
      .max(10)
      .default('en')
      .openapi({
        description: 'ISO language code for keywords and content',
        example: 'en',
      }),
    audience: z
      .string()
      .max(300)
      .optional()
      .openapi({
        description: 'Target reader persona and niche demographics',
        example: 'Software engineers, CTOs, and SaaS founders',
      }),
    modelId: z
      .string()
      .optional()
      .openapi({
        description: 'AI Model identifier override',
        example: 'gpt-4o-mini',
      }),
  })
  .openapi('WizardKeywordsRequest');

export type WizardKeywordsRequest = z.infer<typeof WizardKeywordsRequestSchema>;

export const WizardKeywordsResponseSchema = z
  .object({
    topic: z.string().openapi({ example: 'Cloudflare Workers AI SaaS Architecture' }),
    searchIntent: SearchIntentEnum,
    primaryKeyword: z.string().openapi({
      description: 'Recommended primary focus keyword',
      example: 'cloudflare workers ai saas',
    }),
    secondaryKeywords: z
      .array(z.string())
      .min(1)
      .openapi({
        description: 'Related semantic LSI keywords to target in headings and paragraphs',
        example: [
          'edge serverless architecture',
          'd1 sqlite database',
          'vectorize rag search',
          'workers ai streaming',
          'cloudflare r2 storage',
        ],
      }),
    suggestedAngles: z
      .array(z.string())
      .min(1)
      .openapi({
        description: 'Distinct editorial angles for approaching the article',
        example: [
          'The Complete Engineering Guide to Edge AI Platforms',
          'Why Monolithic SaaS is Dying: The Cloudflare Edge Revolution',
          'Step-by-Step Architecture for Sub-50ms Global AI SaaS',
        ],
      }),
  })
  .openapi('WizardKeywordsResponse');

export type WizardKeywordsResponse = z.infer<typeof WizardKeywordsResponseSchema>;

// =============================================================================
// STEP 2: HEADLINE GENERATION & SEO EVALUATION
// =============================================================================

export const ArticleHeadlineCategoryEnum = z
  .enum(['how_to', 'listicle', 'case_study', 'contrarian', 'ultimate_guide', 'general'])
  .openapi({
    description: 'Editorial formula used for the headline',
    example: 'ultimate_guide',
  });

export type ArticleHeadlineCategory = z.infer<typeof ArticleHeadlineCategoryEnum>;

export const ArticleTitleIdeaSchema = z
  .object({
    title: z.string().openapi({
      description: 'Engaging, SEO-optimized article title',
      example: 'The Ultimate Guide to Building Serverless AI SaaS on Cloudflare Edge',
    }),
    category: ArticleHeadlineCategoryEnum,
    seoScore: z.number().min(0).max(100).openapi({
      description: 'Estimated SEO and readability effectiveness score (0-100)',
      example: 92,
    }),
    characterCount: z.number().openapi({
      description: 'Character length of the title (ideal 50-65 chars for Google SERP)',
      example: 68,
    }),
    estimatedCtr: z.string().openapi({
      description: 'Predicted CTR potential rating',
      example: 'Very High',
    }),
  })
  .openapi('ArticleTitleIdea');

export type ArticleTitleIdea = z.infer<typeof ArticleTitleIdeaSchema>;

export const WizardTitlesRequestSchema = z
  .object({
    topic: z.string().min(2).max(200).openapi({ example: 'Cloudflare Workers AI SaaS' }),
    primaryKeyword: z.string().min(2).max(100).openapi({ example: 'cloudflare workers ai' }),
    secondaryKeywords: z
      .array(z.string())
      .default([])
      .openapi({ example: ['serverless edge', 'd1 database', 'vectorize'] }),
    targetLanguage: z.string().min(2).max(10).default('en').openapi({ example: 'en' }),
    angle: z.string().max(200).optional().openapi({ example: 'Comprehensive architectural guide' }),
    modelId: z.string().optional().openapi({ example: 'gpt-4o-mini' }),
  })
  .openapi('WizardTitlesRequest');

export type WizardTitlesRequest = z.infer<typeof WizardTitlesRequestSchema>;

export const WizardTitlesResponseSchema = z
  .object({
    primaryKeyword: z.string().openapi({ example: 'cloudflare workers ai' }),
    titles: z.array(ArticleTitleIdeaSchema).min(1).openapi({
      description: 'Curated collection of scored title ideas',
    }),
  })
  .openapi('WizardTitlesResponse');

export type WizardTitlesResponse = z.infer<typeof WizardTitlesResponseSchema>;

// =============================================================================
// STEP 3: HIERARCHICAL OUTLINE GENERATION
// =============================================================================

export const ArticleTargetLengthEnum = z
  .enum(['short', 'medium', 'long'])
  .openapi({
    description: 'Target length category: short (~800 words), medium (~1600 words), long (~2800 words)',
    example: 'medium',
  });

export type ArticleTargetLength = z.infer<typeof ArticleTargetLengthEnum>;

export interface ArticleHeading {
  id: string;
  title: string;
  level: 2 | 3;
  keyPoints?: string[];
  estimatedWords?: number;
}

export const ArticleHeadingSchema: z.ZodType<ArticleHeading> = z
  .object({
    id: z.string().openapi({
      description: 'Unique heading identifier',
      example: 'h2_introduction',
    }),
    title: z.string().min(1).openapi({
      description: 'Heading title text (H2 or H3)',
      example: 'Understanding the Edge Computing Revolution',
    }),
    level: z.union([z.literal(2), z.literal(3)]).openapi({
      description: 'Heading level (2 for H2, 3 for H3)',
      example: 2,
    }),
    keyPoints: z
      .array(z.string())
      .optional()
      .openapi({
        description: 'Key concepts to cover in this section',
        example: ['Latency advantages of V8 isolates', 'Global distribution without regions'],
      }),
    estimatedWords: z.number().optional().openapi({
      description: 'Target word count for this specific heading',
      example: 250,
    }),
  })
  .openapi('ArticleHeading');

export const WizardOutlinesRequestSchema = z
  .object({
    title: z.string().min(3).max(250).openapi({
      description: 'Approved article title from Step 2',
      example: 'The Ultimate Guide to Building Serverless AI SaaS on Cloudflare Edge',
    }),
    topic: z.string().max(200).optional().openapi({ example: 'Cloudflare Workers AI SaaS' }),
    primaryKeyword: z.string().min(2).max(100).openapi({ example: 'cloudflare workers ai' }),
    secondaryKeywords: z
      .array(z.string())
      .default([])
      .openapi({ example: ['serverless edge', 'd1 database', 'vectorize'] }),
    targetLanguage: z.string().min(2).max(10).default('en').openapi({ example: 'en' }),
    targetLength: ArticleTargetLengthEnum.default('medium'),
    customInstructions: z
      .string()
      .max(1000)
      .optional()
      .openapi({
        description: 'Specific sections, case studies, or topics to include/avoid',
        example: 'Include a detailed section comparing D1 vs PostgreSQL.',
      }),
    modelId: z.string().optional().openapi({ example: 'gpt-4o-mini' }),
  })
  .openapi('WizardOutlinesRequest');

export type WizardOutlinesRequest = z.infer<typeof WizardOutlinesRequestSchema>;

export const WizardOutlinesResponseSchema = z
  .object({
    title: z.string().openapi({ example: 'The Ultimate Guide to Building Serverless AI SaaS on Cloudflare Edge' }),
    targetLength: ArticleTargetLengthEnum,
    targetWordCount: z.number().openapi({
      description: 'Estimated total word count across all headings',
      example: 1800,
    }),
    headings: z.array(ArticleHeadingSchema).min(2).openapi({
      description: 'Ordered list of headings forming the complete article structure',
    }),
  })
  .openapi('WizardOutlinesResponse');

export type WizardOutlinesResponse = z.infer<typeof WizardOutlinesResponseSchema>;

// =============================================================================
// STEP 4: FULL ARTICLE STREAMING GENERATION
// =============================================================================

export const WizardGenerateRequestSchema = z
  .object({
    workspaceId: z.string().uuid().openapi({
      description: 'Workspace UUID identifier where credits will be deducted',
      example: '123e4567-e89b-12d3-a456-426614174000',
    }),
    title: z.string().min(3).max(250).openapi({
      description: 'Final article title',
      example: 'The Ultimate Guide to Building Serverless AI SaaS on Cloudflare Edge',
    }),
    primaryKeyword: z.string().min(2).max(100).openapi({ example: 'cloudflare workers ai' }),
    secondaryKeywords: z
      .array(z.string())
      .default([])
      .openapi({ example: ['serverless edge', 'd1 database', 'vectorize'] }),
    headings: z
      .array(ArticleHeadingSchema)
      .min(2)
      .openapi({
        description: 'Hierarchical outline array approved by the user',
      }),
    targetLanguage: z.string().min(2).max(10).default('en').openapi({ example: 'en' }),
    modelId: z.string().default('gpt-4o-mini').openapi({ example: 'gpt-4o-mini' }),
    brandVoiceId: z.string().optional().openapi({
      description: 'Optional Brand Voice Profile ID from workspace settings',
      example: 'bv_8f3a12bc90de45f1',
    }),
    toneOverride: z.string().optional().openapi({
      description: 'Temporary tone override for this generation',
      example: 'authoritative',
    }),
    saveDocument: z.boolean().default(true).openapi({
      description: 'Whether to automatically save the completed article into documents table',
      example: true,
    }),
    folderId: z.string().optional().openapi({
      description: 'Optional folder UUID to organize the saved document',
      example: 'fol_123456789',
    }),
    temperature: z.number().min(0).max(1).default(0.7).openapi({ example: 0.7 }),
  })
  .openapi('WizardGenerateRequest');

export type WizardGenerateRequest = z.infer<typeof WizardGenerateRequestSchema>;

/**
 * Standard SSE payload for section progress events.
 */
export const WizardSectionStartPayloadSchema = z.object({
  sectionIndex: z.number().openapi({ example: 0 }),
  sectionTitle: z.string().openapi({ example: 'Understanding the Edge Computing Revolution' }),
  sectionLevel: z.number().openapi({ example: 2 }),
  totalSections: z.number().openapi({ example: 8 }),
});

export const WizardSectionDonePayloadSchema = z.object({
  sectionIndex: z.number().openapi({ example: 0 }),
  sectionTitle: z.string().openapi({ example: 'Understanding the Edge Computing Revolution' }),
  wordCount: z.number().openapi({ example: 245 }),
});

export const WizardDonePayloadSchema = z.object({
  fullContent: z.string().openapi({ description: 'Complete markdown text of the full article' }),
  totalWords: z.number().openapi({ example: 1950 }),
  documentId: z.string().optional().openapi({ example: 'doc_123456789' }),
  generationJobId: z.string().optional().openapi({ example: 'job_123456789' }),
});
